import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["Under $1k", "$1k–$3k", "$3k–$7k", "$7k+", "Not sure"];
const BIZ_TYPES = ["Retail / shop", "Restaurant / cafe", "Beauty / barber", "Fitness / wellness", "Professional services", "Faith / ministry", "Other"];

export default function MarketingIntakeForm() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    business_type: "",
    website: "",
    service: "both",
    budget: "",
    goal: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || sent) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketing-intake`, form);
      setSent(true);
      toast({ title: "Message received", description: "We'll reply within one business day." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-10 text-center" data-testid="mw-intake-success">
        <div className="w-14 h-14 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-[color:var(--dms-navy)]" />
        </div>
        <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-5">Thank you, {form.name.split(" ")[0] || "friend"}.</h3>
        <p className="mt-3 text-[color:var(--dms-navy)]/70 max-w-md mx-auto">We've got your details and we'll reply within one business day with a few questions and some ideas.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-10" data-testid="mw-intake-form">
      <p className="kicker">Marketing &amp; Websites intake</p>
      <h3 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-3">Tell us about your business</h3>
      <p className="mt-3 text-[color:var(--dms-navy)]/65 text-sm">The more we know, the more useful our first reply can be.</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Your name *" testid="mw-field-name">
          <input required value={form.name} onChange={set("name")} className="field" placeholder="Jane Smith" />
        </Field>
        <Field label="Email *" testid="mw-field-email">
          <input required type="email" value={form.email} onChange={set("email")} className="field" placeholder="jane@yourbusiness.com" />
        </Field>
        <Field label="Business name" testid="mw-field-business">
          <input value={form.business_name} onChange={set("business_name")} className="field" placeholder="Jane's Coffee" />
        </Field>
        <Field label="Industry" testid="mw-field-industry">
          <select value={form.business_type} onChange={set("business_type")} className="field">
            <option value="">Pick one</option>
            {BIZ_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Current website (if any)" testid="mw-field-website">
          <input value={form.website} onChange={set("website")} className="field" placeholder="https://..." />
        </Field>
        <Field label="Budget" testid="mw-field-budget">
          <select value={form.budget} onChange={set("budget")} className="field">
            <option value="">Pick one</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
      </div>

      {/* Service picker */}
      <div className="mt-6">
        <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">What do you need? *</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { v: "videos", t: "Social media videos", d: "Short, scroll-stopping content" },
            { v: "website", t: "A new website", d: "Clear design, mobile-first" },
            { v: "both", t: "Both, working together", d: "Videos driving to the site" },
          ].map((s) => (
            <button
              type="button"
              key={s.v}
              onClick={() => setForm({ ...form, service: s.v })}
              data-testid={`mw-service-${s.v}`}
              className={`text-left p-4 rounded-xl border-2 transition ${form.service === s.v ? "border-[color:var(--dms-peach)] bg-[color:var(--dms-peach-light)]/20" : "border-[color:var(--dms-navy)]/15 hover:border-[color:var(--dms-navy)]/35"}`}
            >
              <p className="font-serif text-base text-[color:var(--dms-navy)]">{s.t}</p>
              <p className="text-xs text-[color:var(--dms-navy)]/60 mt-1">{s.d}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <Field label="Main goal in the next 90 days" testid="mw-field-goal">
          <input value={form.goal} onChange={set("goal")} className="field" placeholder="More walk-ins, bookings, online sales…" />
        </Field>
      </div>

      <div className="mt-6">
        <Field label="Anything else we should know?" testid="mw-field-message">
          <textarea rows={4} value={form.message} onChange={set("message")} className="field" placeholder="Your audience, your style, the kind of videos you love, deadlines…" />
        </Field>
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
        <p className="text-xs text-[color:var(--dms-navy)]/55">We reply within one business day — no automated bots.</p>
        <button type="submit" disabled={submitting} className="btn-peach" data-testid="mw-intake-submit">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send my details <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>

      <style>{`
        .field { width: 100%; border: 1px solid rgba(26,47,77,0.18); background: #fff; padding: 12px 14px; border-radius: 12px; font-size: 14px; color: #1a2f4d; outline: none; transition: border-color .2s; font-family: 'Inter', sans-serif; }
        .field:focus { border-color: var(--dms-peach); box-shadow: 0 0 0 3px rgba(212,165,116,0.15); }
      `}</style>
    </form>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
